import { Switch, Route, useLocation } from "wouter";
import { useEffect } from "react";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/lib/auth-context";
import { ProtectedRoute } from "@/components/protected-route";
import { AppLayout } from "@/components/layout/app-layout";
import { TrialWall } from "@/components/trial-wall";
import { orgVeAssinatura } from "@shared/org-status";
import NotFound from "@/pages/not-found";
import LoginPage from "@/pages/login";
import DefinirSenhaPage from "@/pages/definir-senha";
import EsqueciSenhaPage from "@/pages/esqueci-senha";
import LandingPage from "@/pages/landing";
import SignupPage from "@/pages/signup";
import { TermosPage, PrivacidadePage } from "@/pages/documento-legal";
import RomaneioPublicoPage from "@/pages/publico/romaneio-publico";
import CriarAcessoPage from "@/pages/publico/criar-acesso";
import OfertaPublicaPage from "@/pages/publico/oferta-publica";
import CargaPublicaPage from "@/pages/publico/carga-publica";
import ItemPublicoPage from "@/pages/publico/item-publico";
import AdotarPage from "@/pages/publico/adotar";
import MercadoPage from "@/pages/mercado/index";
import MercadoCotacoes from "@/pages/mercado/cotacoes";
import MercadoOfertas from "@/pages/mercado/produtor/ofertas";
import MercadoOfertaNova from "@/pages/mercado/produtor/oferta-nova";
import MercadoOfertaDetalhe from "@/pages/mercado/produtor/oferta-detalhe";
import CompradorPraca from "@/pages/mercado/comprador/praca";
import MercadoCompradores from "@/pages/mercado/produtor/compradores";
import MercadoCompradorDetalhe from "@/pages/mercado/produtor/comprador-detalhe";
import CompradorRomaneios from "@/pages/mercado/comprador/romaneios";
import CompradorRomaneioForm from "@/pages/mercado/comprador/romaneio-form";
import MercadoCargas from "@/pages/mercado/comprador/cargas";
import MercadoCarga from "@/pages/mercado/comprador/carga";
import MercadoCargaDocumento from "@/pages/mercado/comprador/carga-documento";
import MercadoRegistrarProdutor from "@/pages/mercado/comprador/registrar";
import MercadoProdutoresComprador from "@/pages/mercado/comprador/produtores";
import MercadoAPagar from "@/pages/mercado/comprador/pagar";
import MercadoReceberPage from "@/pages/mercado/receber";
import { rotaInicialDoUsuario, LAST_MODULE_KEY } from "@/lib/modules";
import { OFERTAS_ATIVAS } from "@shared/mercado-ofertas";
import BillingPage from "@/pages/billing";
import Dashboard from "@/pages/dashboard";
import MangarasPage from "@/pages/mangaras";
import ColheitasPage from "@/pages/colheitas";
import VendasPage from "@/pages/vendas";
import VendaFormPage from "@/pages/venda-form";
import VendaDetalhePage from "@/pages/venda-detalhe";
import CiclosPage from "@/pages/ciclos";
import AnaliseCicloPage from "@/pages/analise-ciclo";
import RelatoriosPage from "@/pages/relatorios";
import RelatorioCachosColherPage from "@/pages/relatorio-cachos-colher";
import RelatoriosPerformanceSetorPage from "@/pages/relatorios-performance-setor";
import RelatoriosProdutividadeFamiliaPage from "@/pages/relatorios-produtividade-familia";
import RelatoriosPrevistoRealizadoPage from "@/pages/relatorios-previsto-realizado";
import RelatoriosRendimentoSetorPage from "@/pages/relatorios-rendimento-setor";
import VariedadesPage from "@/pages/variedades";
import SetoresPage from "@/pages/setores";
import CoresPage from "@/pages/cores";
import ImportarPage from "@/pages/importar";
import ConfiguracoesPage from "@/pages/configuracoes";
import MotivosPerdasPage from "@/pages/motivos-perdas";
import UsuariosPage from "@/pages/usuarios";
import InicioPage from "@/pages/inicio";
import EquipeDashboard from "@/pages/equipe/dashboard";
import EquipeHoje from "@/pages/equipe/hoje";
import PlanoDaSemana from "@/pages/equipe/plano-da-semana";
import EquipeFinanceiro from "@/pages/equipe/financeiro";
import EquipeFicha from "@/pages/equipe/ficha";
import EquipeTv from "@/pages/equipe/tv";
import EquipeFuncionarios from "@/pages/equipe/funcionarios";
import EquipeAtividades from "@/pages/equipe/atividades";
import EquipeRemuneracao from "@/pages/equipe/remuneracao";
import EquipeRelatorios from "@/pages/equipe/relatorios";
import EquipeAlocacao from "@/pages/equipe/alocacao";
import AdminCotacoesPage from "@/pages/admin/cotacoes";
import AdminPracasPage from "@/pages/admin/pracas";
import AdminOrganizacoesPage from "@/pages/admin/organizacoes";
import PlataformaVisaoGeral from "@/pages/plataforma/index";
import PlataformaFazendas from "@/pages/plataforma/fazendas";
import PlataformaFazenda from "@/pages/plataforma/fazenda";
import PlataformaMensagens from "@/pages/plataforma/mensagens";
import PlataformaMensagemEditar from "@/pages/plataforma/mensagem-editar";
import ContaAvisos from "@/pages/conta-avisos";
import { refDaRota } from "@shared/mercado-romaneios";
import AgentePage from "@/pages/agente";
import ManejoOperacoes from "@/pages/manejo/operacoes";
import ManejoPrograma from "@/pages/manejo/programa";
import ManejoIrrigacao from "@/pages/manejo/irrigacao";
import AuditoriaLista from "@/pages/auditoria/index";
import AuditoriaDetalhe from "@/pages/auditoria/auditoria";
import AuditoriaCriterios from "@/pages/auditoria/criterios";
import AuditoriaFaixas from "@/pages/auditoria/faixas";
import AuditoriaDashboard from "@/pages/auditoria/dashboard";

function HomePage() {
  const { isAuthenticated, isLoading, modulesEnabled } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    // A MESMA decisão do login e do portão de módulo, numa função só
    // (`rotaInicialDoUsuario`): três cópias dela é como o `/cacho/inicio` cravado do
    // `login.tsx` sobreviveu ao fix que esta raiz já tinha.
    let ultimo: string | null = null;
    try {
      ultimo = localStorage.getItem(LAST_MODULE_KEY);
    } catch {
      // Sem localStorage (aba privada) só se perde o "onde parei" — não é motivo pra parar.
    }
    const destino = rotaInicialDoUsuario(modulesEnabled, ultimo);
    // Org sem módulo nenhum não tem para onde ir: fica na raiz, que renderiza o aviso
    // abaixo em vez de um redirect em círculo.
    if (destino) setLocation(destino, { replace: true } as any);
  }, [isAuthenticated, isLoading, modulesEnabled]);

  if (isLoading) return null;
  if (!isAuthenticated) return <LandingPage />;
  if (rotaInicialDoUsuario(modulesEnabled, null) === null) return <SemModulo />;
  return null; // redirect via useEffect acima
}

/**
 * Org sem NENHUM módulo habilitado. Não deveria acontecer — e quando acontecer, a tela
 * diz o que é em vez de piscar em branco ou redirecionar em círculo.
 */
function SemModulo() {
  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-sm text-center space-y-2">
        <p className="font-medium">Nenhum módulo liberado para esta organização.</p>
        <p className="text-sm text-muted-foreground">
          Fale com quem administra a conta para liberar o acesso.
        </p>
      </div>
    </div>
  );
}

/**
 * Rota de uma feature DESLIGADA (hoje: Ofertas e a Praça do comprador).
 *
 * Manda para a home do Mercado em vez de mostrar erro: quem chega aqui veio de
 * um link salvo, de um histórico do navegador ou da barra de endereço, e não
 * fez nada errado. A metade que importa não é esta — é a API recusar junto
 * (`server/routes/mercado/index.ts`); esconder a tela com a rota viva seria
 * deixar a porta aberta.
 */
function RotaDeOferta({ children }: { children: React.ReactNode }) {
  if (!OFERTAS_ATIVAS) return <RedirecionaPara para="/mercado" />;
  return <>{children}</>;
}

/** Redirect client-side de caminho antigo — `replace` para não sujar o histórico. */
function RedirecionaPara({ para }: { para: string }) {
  const [, setLocation] = useLocation();
  useEffect(() => { setLocation(para, { replace: true } as any); }, [para]);
  return null;
}

function Router() {
  const { isBlocked, user } = useAuth();

  return (
    <>
      {/* Org em CORTESIA nunca vê a parede de "assine" (lançamento gratuito). O
          servidor já não bloqueia cortesia; isto cobre a janela de até 60 s do
          cache do `trialMiddleware` depois que a Plataforma (ou o script de
          regularização) tira o prazo de uma org. */}
      {isBlocked && orgVeAssinatura(user?.orgStatus) && <TrialWall />}
      <Switch>
        {/* ── Rotas públicas ─────────────────────────────────────────── */}
        <Route path="/login" component={LoginPage} />
        {/* Convite e reset por link (PR 3): sem sessão, sem layout. */}
        <Route path="/definir-senha" component={DefinirSenhaPage} />
        <Route path="/esqueci-senha" component={EsqueciSenhaPage} />
        <Route path="/signup" component={SignupPage} />
        {/* Documentos do site (rodapé da landing). Placeholder até a revisão
            jurídica — ver pages/documento-legal.tsx. */}
        <Route path="/termos" component={TermosPage} />
        <Route path="/privacidade" component={PrivacidadePage} />

        {/* Link público do romaneio (Mercado C1). FORA do ProtectedRoute e sem
            AppLayout: quem abre é o corretor, que não tem conta no Rizoma — conta de
            corretor é o C2. Sem menu, sem sidebar, sem trial wall. */}
        <Route path="/v/:token/criar-acesso" component={CriarAcessoPage} />
        <Route path="/v/:token" component={RomaneioPublicoPage} />

        {/* Link público da OFERTA (PR 3). Mesma família do /v/:token: sem conta,
            sem menu e sem trial wall — quem abre é o comprador da praça, que na
            primeira vez não tem (e não vai criar) conta para dizer quantos kg quer. */}
        <Route path="/o/:token" component={OfertaPublicaPage} />

        {/* C5b — os DOIS links do comprador, fora do gate de autenticação como
            o `/v/` e o `/o/`: quem abre não tem conta no Rizoma, e é por isso que
            o link existe. `/criar-acesso` ANTES do link puro: o Switch do wouter
            para no primeiro match. */}
        <Route path="/c/:token/criar-acesso">
          {() => <AdotarPage alvo="destino" />}
        </Route>
        <Route path="/c/:token" component={CargaPublicaPage} />
        <Route path="/p/:token/criar-acesso">
          {() => <AdotarPage alvo="produtor" />}
        </Route>
        <Route path="/p/:token" component={ItemPublicoPage} />

        {/* ── Home (redireciona para último módulo) ──────────────────── */}
        <Route path="/">
          {() => <HomePage />}
        </Route>

        {/* ── Módulo Mercado (/mercado/*) ─────────────────────────────
            A MESMA rota serve produtor e corretor; quem decide o conteúdo é
            `organizations.tipo`, dentro da página. Duas URLs para "a home do
            Mercado" seriam dois lugares para o link errado apontar. */}
        {/* ── Romaneios do comprador ────────────────────────────────────
            `/novo` e `/:ref/editar` ANTES de `/:ref`: o Switch do wouter para no
            PRIMEIRO match, e a rota curta engoliria as folhas.

            ⚠️ `/:ref` monta a MESMA página da lista, que renderiza o detalhe
            como painel por cima. Não é economia de componente: a lista fica
            atrás, e voltar do detalhe devolve os filtros porque eles estão na
            query string da entrada anterior do histórico. */}
        <Route path="/mercado/romaneios/novo">
          {() => (
            <ProtectedRoute cap="mercado.corretor.operar">
              <AppLayout>
                <CompradorRomaneioForm />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/mercado/romaneios/:ref/editar">
          {(params: { ref: string }) => (
            <ProtectedRoute cap="mercado.corretor.operar">
              <AppLayout>
                <CompradorRomaneioForm refRomaneio={refDaRota(params.ref)} />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/mercado/romaneios/:ref">
          {() => (
            <ProtectedRoute cap="mercado.corretor.ver">
              <AppLayout>
                <CompradorRomaneios />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        {/* ── Visão COMPRADOR · C5a — cargas e carteira ─────────────────
            `/registrar` ANTES de `/:id`, e `/:id` antes da lista: o Switch do
            wouter para no PRIMEIRO match, e a rota curta engoliria as folhas. */}
        <Route path="/mercado/cargas/:id/registrar">
          {(params: { id: string }) => (
            <ProtectedRoute>
              <AppLayout>
                <MercadoRegistrarProdutor cargaId={params.id} />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        {/* `/documento` ANTES de `/:id`: o Switch do wouter para no PRIMEIRO
            match, e a rota curta engoliria a folha. */}
        <Route path="/mercado/cargas/:id/documento">
          {(params: { id: string }) => (
            <ProtectedRoute cap="mercado.corretor.ver">
              <AppLayout>
                <MercadoCargaDocumento id={params.id} />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/mercado/cargas/:id">
          {(params: { id: string }) => (
            <ProtectedRoute>
              <AppLayout>
                <MercadoCarga id={params.id} />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/mercado/cargas">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <MercadoCargas />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        {/* C4 — o que o comprador deve. `/mercado/receber` é a tela do PRODUTOR
            (o que ELE tem a receber): nomes diferentes porque são os dois lados
            da mesma dívida. */}
        <Route path="/mercado/pagar">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <MercadoAPagar />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        {/* A carteira do comprador. `/mercado/compradores` é a do PRODUTOR —
            nomes diferentes porque são listas de lados opostos do balcão. */}
        <Route path="/mercado/produtores">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <MercadoProdutoresComprador />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/mercado/praca">
          {() => (
            <RotaDeOferta>
              <ProtectedRoute>
                <AppLayout>
                  <CompradorPraca />
                </AppLayout>
              </ProtectedRoute>
            </RotaDeOferta>
          )}
        </Route>

        <Route path="/mercado/romaneios">
          {() => (
            <ProtectedRoute cap="mercado.corretor.ver">
              <AppLayout>
                <CompradorRomaneios />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        {/* Visão do PRODUTOR (PR 1 da UI): cinco telas, cada uma com a sua rota.
            Cotações e Ofertas são placeholders honestos até os PRs 2 e 3. */}
        <Route path="/mercado/cotacoes">
          {() => (
            <ProtectedRoute cap="mercado.ver">
              <AppLayout>
                <MercadoCotacoes />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        {/* `/nova` ANTES de `/ofertas`: o Switch do wouter para no primeiro
            match, e a rota curta engoliria a folha de criação. */}
        <Route path="/mercado/ofertas/nova">
          {() => (
            <RotaDeOferta>
              <ProtectedRoute cap="mercado.ver">
                <AppLayout>
                  <MercadoOfertaNova />
                </AppLayout>
              </ProtectedRoute>
            </RotaDeOferta>
          )}
        </Route>

        {/* `/:id` depois de `/nova` e antes da lista, pela mesma razão: o
            Switch para no primeiro match. */}
        <Route path="/mercado/ofertas/:id">
          {() => (
            <RotaDeOferta>
              <ProtectedRoute cap="mercado.ver">
                <AppLayout>
                  <MercadoOfertaDetalhe />
                </AppLayout>
              </ProtectedRoute>
            </RotaDeOferta>
          )}
        </Route>

        <Route path="/mercado/ofertas">
          {() => (
            <RotaDeOferta>
              <ProtectedRoute cap="mercado.ver">
                <AppLayout>
                  <MercadoOfertas />
                </AppLayout>
              </ProtectedRoute>
            </RotaDeOferta>
          )}
        </Route>

        <Route path="/mercado/compradores/:compradorId">
          {(params: { compradorId: string }) => (
            <ProtectedRoute cap="mercado.ver">
              <AppLayout>
                <MercadoCompradorDetalhe compradorId={params.compradorId} />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/mercado/compradores">
          {() => (
            <ProtectedRoute cap="mercado.ver">
              <AppLayout>
                <MercadoCompradores />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        {/* Os caminhos antigos (C1/C2) continuam existindo e redirecionam: o
            produtor já mandou link de "/mercado/corretores" por WhatsApp, e o
            navegador dele guardou o histórico. `replace` para o caminho velho não
            ficar no botão Voltar. */}
        <Route path="/mercado/corretores/:compradorId">
          {(params: { compradorId: string }) => <RedirecionaPara para={`/mercado/compradores/${params.compradorId}`} />}
        </Route>
        <Route path="/mercado/corretores">
          {() => <RedirecionaPara para="/mercado/compradores" />}
        </Route>

        {/* C8b — "A receber" é a MESMA rota dos dois lados, como a home: o
            produtor vê o que os compradores lhe devem; o corretor, o que os
            destinos devem por carga entregue. Quem decide é `organizations.tipo`,
            dentro da página. */}
        <Route path="/mercado/receber">
          {() => (
            <ProtectedRoute cap="mercado.ver">
              <AppLayout>
                <MercadoReceberPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/mercado">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <MercadoPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        {/* ── Módulo Cacho — conteúdo e cadastros (/cacho/*) ─────────── */}
        <Route path="/cacho">
          {() => { window.location.replace("/cacho/inicio"); return null; }}
        </Route>

        <Route path="/cacho/inicio">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <InicioPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/dashboard">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <Dashboard />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/mangaras">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <MangarasPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/colheitas">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <ColheitasPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/vendas/novo">
          {() => (
            <ProtectedRoute cap="cacho.vendas.ver">
              <AppLayout>
                <VendaFormPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/vendas/:id/editar">
          {() => (
            <ProtectedRoute cap="cacho.vendas.ver">
              <AppLayout>
                <VendaFormPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/vendas/:id">
          {() => (
            <ProtectedRoute cap="cacho.vendas.ver">
              <AppLayout>
                <VendaDetalhePage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/vendas">
          {() => (
            <ProtectedRoute cap="cacho.vendas.ver">
              <AppLayout>
                <VendasPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/ciclos/:id">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <AnaliseCicloPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/ciclos">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <CiclosPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/relatorios/cachos-colher">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <RelatorioCachosColherPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/relatorios/performance-setor">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <RelatoriosPerformanceSetorPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/relatorios/produtividade-familia">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <RelatoriosProdutividadeFamiliaPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/relatorios/previsto-realizado">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <RelatoriosPrevistoRealizadoPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/relatorios/rendimento-por-setor">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <RelatoriosRendimentoSetorPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/relatorios">
          {() => (
            <ProtectedRoute>
              <AppLayout>
                <RelatoriosPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/variedades">
          {() => (
            <ProtectedRoute cap="cacho.cadastros">
              <AppLayout>
                <VariedadesPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/setores">
          {() => (
            <ProtectedRoute cap="cacho.cadastros">
              <AppLayout>
                <SetoresPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/cores">
          {() => (
            <ProtectedRoute cap="cacho.cadastros">
              <AppLayout>
                <CoresPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/motivos-perdas">
          {() => (
            <ProtectedRoute cap="cacho.cadastros">
              <AppLayout>
                <MotivosPerdasPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/cacho/importar">
          {() => (
            <ProtectedRoute cap="cacho.cadastros">
              <AppLayout>
                <ImportarPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        {/* ── Transversais (/admin/*) ────────────────────────────────── */}
        <Route path="/admin/usuarios">
          {() => (
            <ProtectedRoute cap="org.usuarios">
              <AppLayout>
                <UsuariosPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/admin/configuracoes">
          {() => (
            <ProtectedRoute cap="org.configurar">
              <AppLayout>
                <ConfiguracoesPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/admin/assinatura">
          {() => (
            <ProtectedRoute cap="org.assinatura">
              <AppLayout>
                <BillingPage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        {/* ── Módulo Plataforma (/plataforma/*) — quem administra o Rizoma ──
            Gate: `users.plataforma_admin` (ProtectedRoute lê `isPlataforma`); a API
            recusa com `requirePlataforma()`. Cotações, Praças e o pré-cadastro
            moravam sob /admin — os caminhos antigos redirecionam abaixo. */}
        <Route path="/plataforma/fazendas/:id">
          {() => (
            <ProtectedRoute plataforma>
              <AppLayout><PlataformaFazenda /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/plataforma/fazendas">
          {() => (
            <ProtectedRoute plataforma>
              <AppLayout><PlataformaFazendas /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/plataforma/cotacoes">
          {() => (
            <ProtectedRoute plataforma>
              <AppLayout><AdminCotacoesPage /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/plataforma/pracas">
          {() => (
            <ProtectedRoute plataforma>
              <AppLayout><AdminPracasPage /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/plataforma/organizacoes">
          {() => (
            <ProtectedRoute plataforma>
              <AppLayout><AdminOrganizacoesPage /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/plataforma/mensagens/:chave">
          {() => (
            <ProtectedRoute plataforma>
              <AppLayout><PlataformaMensagemEditar /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/plataforma/mensagens">
          {() => (
            <ProtectedRoute plataforma>
              <AppLayout><PlataformaMensagens /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>
        <Route path="/plataforma">
          {() => (
            <ProtectedRoute plataforma>
              <AppLayout><PlataformaVisaoGeral /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>
        {/* Meus avisos — a preferência é da pessoa, de qualquer papel (menu da conta). */}
        <Route path="/conta/avisos">
          {() => (
            <ProtectedRoute>
              <AppLayout><ContaAvisos /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>
        {/* Caminhos antigos das telas de plataforma — `replace`, sem sujar o histórico. */}
        <Route path="/admin/cotacoes">{() => <RedirecionaPara para="/plataforma/cotacoes" />}</Route>
        <Route path="/admin/pracas">{() => <RedirecionaPara para="/plataforma/pracas" />}</Route>
        <Route path="/admin/organizacoes">{() => <RedirecionaPara para="/plataforma/organizacoes" />}</Route>

        {/* ── Módulo Equipe — TV sem layout, demais com AppLayout ─────── */}
        <Route path="/equipe/tv">
          {() => (
            <ProtectedRoute>
              <EquipeTv />
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/equipe/hoje">
          {() => (
            <ProtectedRoute>
              <AppLayout><EquipeHoje /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/equipe/plano">
          {() => (
            <ProtectedRoute>
              <AppLayout><PlanoDaSemana /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        {/* A tela do kanban do Equipe foi absorvida pelo Plano da semana; o
            arquivo da tela foi apagado na Onda 4 (era código morto: 650 linhas
            que o app nunca renderizou). Esta rota NÃO é sobra: ela existe
            para o link salvo e o favorito não quebrarem — mesmo padrão de
            /manejo/kanban → /manejo/operacoes abaixo. */}
        <Route path="/equipe/kanban">
          {() => { window.location.replace("/equipe/plano"); return null; }}
        </Route>

        <Route path="/equipe/financeiro">
          {() => (
            <ProtectedRoute cap="equipe.folha.ver">
              <AppLayout><EquipeFinanceiro /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/equipe/ficha/:funcionarioId">
          {() => (
            <ProtectedRoute>
              <AppLayout><EquipeFicha /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/equipe/funcionarios">
          {() => (
            <ProtectedRoute cap="equipe.funcionarios.gerir">
              <AppLayout><EquipeFuncionarios /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/equipe/atividades">
          {() => (
            <ProtectedRoute cap="equipe.funcionarios.gerir">
              <AppLayout><EquipeAtividades /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/equipe/remuneracao">
          {() => (
            <ProtectedRoute cap="equipe.remuneracao.configurar">
              <AppLayout><EquipeRemuneracao /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/equipe/relatorios">
          {() => (
            <ProtectedRoute cap="equipe.folha.ver">
              <AppLayout><EquipeRelatorios /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/equipe/alocacao">
          {() => (
            <ProtectedRoute cap="equipe.folha.ver">
              <AppLayout><EquipeAlocacao /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/equipe">
          {() => (
            <ProtectedRoute>
              <AppLayout><EquipeDashboard /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        {/* ── Módulo Manejo ─────────────────────────────────────────── */}
        {/* Sprint 2 §2.4: kanban é uma VISÃO, não a entidade — a rota canônica
            agora é /manejo/operacoes; /manejo/kanban só redireciona (link salvo
            não quebra), mesmo padrão de /equipe/kanban → /equipe/plano acima. */}
        <Route path="/manejo/kanban">
          {() => { window.location.replace("/manejo/operacoes"); return null; }}
        </Route>

        <Route path="/manejo/operacoes">
          {() => (
            <ProtectedRoute>
              <AppLayout><ManejoOperacoes /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/manejo/irrigacao">
          {() => (
            <ProtectedRoute>
              <AppLayout><ManejoIrrigacao /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/manejo/programa">
          {() => (
            <ProtectedRoute cap="manejo.planejar">
              <AppLayout><ManejoPrograma /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        {/* Auditoria (nota por critério, sem dinheiro) mora dentro do Manejo —
            não é módulo próprio, usa o mesmo gate "manejo". Critérios, Faixas
            e Dashboard são cadastro/análise: rotas próprias, mas o item de nav
            vive em Administração (client/src/components/app-sidebar.tsx). */}
        <Route path="/manejo/auditoria/criterios">
          {() => (
            <ProtectedRoute cap="auditoria.configurar">
              <AppLayout><AuditoriaCriterios /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/manejo/auditoria/faixas">
          {() => (
            <ProtectedRoute cap="auditoria.configurar">
              <AppLayout><AuditoriaFaixas /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/manejo/auditoria/dashboard">
          {() => (
            <ProtectedRoute cap="auditoria.ver">
              <AppLayout><AuditoriaDashboard /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/manejo/auditoria/:id">
          {() => (
            <ProtectedRoute cap="auditoria.ver">
              <AppLayout><AuditoriaDetalhe /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        <Route path="/manejo/auditoria">
          {() => (
            <ProtectedRoute cap="auditoria.ver">
              <AppLayout><AuditoriaLista /></AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        {/* Etapa 1 §6: o chat saiu do Manejo e virou tela própria. /manejo só
            redireciona — link salvo e o hábito de "Comando" não quebram, mesmo
            padrão de /manejo/kanban → /manejo/operacoes acima. */}
        <Route path="/manejo">
          {() => { window.location.replace("/agente"); return null; }}
        </Route>

        {/* ── Plataforma ─────────────────────────────────────────────── */}
        {/* /agente é rota de PLATAFORMA (como /admin), não módulo: fora do
            registry de módulos e do gating por plano. Acesso admin/gestor. */}
        <Route path="/agente">
          {() => (
            <ProtectedRoute cap="agente.usar">
              <AppLayout>
                <AgentePage />
              </AppLayout>
            </ProtectedRoute>
          )}
        </Route>

        {/* ── Rede de segurança: redirects client-side de paths antigos ─ */}
        <Route path="/inicio">
          {() => { window.location.replace("/cacho/inicio"); return null; }}
        </Route>
        <Route path="/dashboard">
          {() => { window.location.replace("/cacho/dashboard"); return null; }}
        </Route>
        <Route path="/mangaras">
          {() => { window.location.replace("/cacho/mangaras"); return null; }}
        </Route>
        <Route path="/colheitas">
          {() => { window.location.replace("/cacho/colheitas"); return null; }}
        </Route>
        <Route path="/ciclos/:id">
          {() => { window.location.replace("/cacho" + window.location.pathname); return null; }}
        </Route>
        <Route path="/ciclos">
          {() => { window.location.replace("/cacho/ciclos"); return null; }}
        </Route>
        <Route path="/relatorios/cachos-colher">
          {() => { window.location.replace("/cacho/relatorios/cachos-colher"); return null; }}
        </Route>
        <Route path="/relatorios/performance-setor">
          {() => { window.location.replace("/cacho/relatorios/performance-setor"); return null; }}
        </Route>
        <Route path="/relatorios/produtividade-familia">
          {() => { window.location.replace("/cacho/relatorios/produtividade-familia"); return null; }}
        </Route>
        <Route path="/relatorios/previsto-realizado">
          {() => { window.location.replace("/cacho/relatorios/previsto-realizado"); return null; }}
        </Route>
        <Route path="/relatorios/rendimento-por-setor">
          {() => { window.location.replace("/cacho/relatorios/rendimento-por-setor"); return null; }}
        </Route>
        <Route path="/relatorios">
          {() => { window.location.replace("/cacho/relatorios"); return null; }}
        </Route>
        <Route path="/variedades">
          {() => { window.location.replace("/cacho/variedades"); return null; }}
        </Route>
        <Route path="/setores">
          {() => { window.location.replace("/cacho/setores"); return null; }}
        </Route>
        <Route path="/cores">
          {() => { window.location.replace("/cacho/cores"); return null; }}
        </Route>
        <Route path="/motivos-perdas">
          {() => { window.location.replace("/cacho/motivos-perdas"); return null; }}
        </Route>
        <Route path="/importar">
          {() => { window.location.replace("/cacho/importar"); return null; }}
        </Route>
        <Route path="/configuracoes/billing">
          {() => { window.location.replace("/admin/assinatura"); return null; }}
        </Route>
        <Route path="/configuracoes">
          {() => { window.location.replace("/admin/configuracoes"); return null; }}
        </Route>
        <Route path="/usuarios">
          {() => { window.location.replace("/admin/usuarios"); return null; }}
        </Route>

        <Route component={NotFound} />
      </Switch>
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Router />
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
